import type {AnyAction, Dispatch} from 'redux';

import {hasValue} from '@famly/stat_ts-utils_has-value';
import BehaviorsCollection from 'web-app/behaviors/behaviors-collection';
import PricingGroupBehaviors from 'web-app/behaviors/pricing-group-behavior-ids';
import type {FetchPricingGroupsQuery} from 'web-app/react/bookings-finance/hooks/__generated__/fetch-pricing-groups.query.api-types';
import * as ChildActions from 'web-app/react/entities/children/actions';
import ChildPlanBehaviorsEntity from 'web-app/react/entities/plans/child-plans-behaviors-entity';
import * as ManagePlanActions from 'web-app/react/plans/manage-plan/actions';
import * as ManagePlanHelpers from 'web-app/react/plans/manage-plan/helpers';
import * as ChildProfileSelectors from 'web-app/react/profile/child-profile/plans-and-invoices/selectors';
import type {RootState} from 'web-app/react/redux/root-state';
import {TrackingEvents} from 'web-app/util/famlytics/events';
import {head, type NonEmptyArray} from 'web-app/util/non-empty-array';
import {openEditPlanModal} from 'web-app/react/routes/account/institution/finance/actions';

export const setUpLegacyPlanModal = ({
    dispatchReduxAction,
    reduxState,
    siteId,
    selectedChildIds,
    pricingGroupsData,
}: {
    dispatchReduxAction: Dispatch<AnyAction>;
    reduxState: RootState;
    siteId: string;
    selectedChildIds: NonEmptyArray<string>;
    pricingGroupsData: FetchPricingGroupsQuery | undefined;
}) => {
    const firstChildId = head(selectedChildIds);

    const child = ChildProfileSelectors.child(reduxState, {childId: firstChildId});
    const pricingGroups =
        pricingGroupsData?.finance.pricingGroups.list.filter(pricingGroup => !pricingGroup.deleted) ?? [];
    const canUseAutomaticPricingGroup = hasValue(
        pricingGroupsData?.finance.pricingGroups.behaviors.find(
            behavior => behavior.behaviorId === PricingGroupBehaviors.CanChooseAutoPricingGroup,
        ),
    );
    const pricingGroupId = pricingGroups
        ? ManagePlanHelpers.getAutoPricingGroupId(canUseAutomaticPricingGroup, pricingGroups)
        : null;
    const emptyBehaviors = new BehaviorsCollection([]);
    const behaviors = ChildPlanBehaviorsEntity.selectors.getById(reduxState, {id: siteId});
    const childPlanBehaviors = behaviors ? behaviors.behaviors : emptyBehaviors;

    const handleLegacyPlanModalOpen = () => {
        if (!hasValue(child?.localStartDate)) {
            dispatchReduxAction(ChildActions.fetch.action(firstChildId));
        }

        dispatchReduxAction(
            ManagePlanActions.showCreatePlan.action({
                institutionId: siteId,
                childPlanBehaviors,
                childId: firstChildId,
                validTo: undefined,
                validOnMustBeAfter: child?.localStartDate,
                validToMustBeAfter: child?.localStartDate,
                trackingContext: TrackingEvents.CREATE_PLAN,
                pricingGroupId,
            }),
        );
    };

    const storeChildDetails = reduxState.entities.children.get(firstChildId);

    const handleLegacyEditPlanModalOpen = (childStartDate: string, planId: string) => {
        // the plans modal expects some specific child details to be in the redux state
        // if these details are missing then the "delete" button won't work and might cause a fatal runtime error.
        if (!storeChildDetails) {
            dispatchReduxAction(ChildActions.fetch.action(firstChildId));
        }

        dispatchReduxAction(openEditPlanModal.action(siteId, firstChildId, childStartDate, planId));
    };

    return {handleLegacyPlanModalOpen, handleLegacyEditPlanModalOpen};
};
